import React from "react";
import { Typography, theme } from "antd";
import { space } from "../theme/space";

const { Text } = Typography;

/**
 * The surfaces every reading screen is built from.
 *
 * One rule holds them together: **a boundary is drawn only where there is a
 * real boundary.** A card inside a card is two borders arguing about the same
 * edge, so a panel is one surface with hairline dividers inside it, and a group
 * of related figures is one panel — not one card per figure.
 *
 * These started inside the Reports screens and moved here when Analytics was
 * rebuilt on them, following `theme/space` and `components/PageHeader` out of
 * the same file for the same reason: they are not a property of Reports, and a
 * second copy would have drifted the first time one page wanted a different
 * border. `pages/Reports/ui.tsx` re-exports them, so those imports read exactly
 * as they always did.
 */

/** A single elevated surface. No shadow — a hairline is enough at this density. */
export const Panel: React.FC<{
  children: React.ReactNode;
  padding?: number;
  style?: React.CSSProperties;
}> = ({ children, padding = space.base, style }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/**
 * The quiet all-caps label above a group of controls or a panel section.
 *
 * Small, letterspaced and muted on purpose: it must be findable when scanned
 * and invisible when read past. A heading here would compete with the page
 * title for the same job.
 */
export const SectionLabel: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ children, style }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: token.colorTextTertiary,
        lineHeight: 1.4,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

/** A hairline rule, used instead of nesting another bordered box. */
export const Divider: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        height: 1,
        background: token.colorSplit,
        border: 0,
        ...style,
      }}
    />
  );
};

/**
 * One headline number.
 *
 * Proportional figures, deliberately: `tabular-nums` gives every digit the
 * width of a zero, which makes a number like 121 look loose at display sizes.
 * Tabular figures belong in columns that must align, and are applied there.
 */
export const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  emphasis?: boolean;
}> = ({ label, value, hint, emphasis }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ minWidth: 96 }}>
      <SectionLabel>{label}</SectionLabel>
      <div
        style={{
          fontSize: emphasis ? 30 : 22,
          fontWeight: 600,
          lineHeight: 1.15,
          marginTop: space.hair,
          color: token.colorText,
          fontVariantNumeric: "proportional-nums",
        }}
      >
        {value}
      </div>
      {hint && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {hint}
        </Text>
      )}
    </div>
  );
};

/**
 * A labelled control in a filter row.
 *
 * The label sits above rather than beside so every control in the row shares
 * one baseline, whatever its width — the thing that makes a filter bar read as
 * a row instead of a scatter.
 */
export const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, children, style }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: space.hair, ...style }}>
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);
